import { useEffect, useState } from 'react';
import { BellRing, Check } from 'lucide-react';
/*
  El trabajador del aviso se empaqueta con huella en el nombre (`/assets/sw-avisos-<huella>.js`): si se
  corrige, el nombre cambia y el celular toma el nuevo aunque Cloudflare guarde copias del viejo. Y vive
  en `/assets/`, así que su alcance no toca ninguna pantalla del personal. Que no se incruste como `data:`
  lo asegura `vite.config.ts` (`assetsInlineLimit`).
*/
import urlTrabajador from './sw-avisos.js?url';

/**
 * Sprint 163 — "Avísame cuando mi pedido salga", en la página del pedido del cliente.
 *
 * Se pregunta en el momento justo y una sola vez: si el navegador pregunta "¿permitir avisos?" en frío
 * y el cliente dice que no, ya no se le puede volver a preguntar. Por eso el botón explica para qué es
 * y el permiso se pide sólo al tocarlo.
 *
 * No aparece si el negocio no tiene el aviso encendido, si el pedido ya salió, o si el navegador no
 * puede recibir avisos (iPhone sin la página guardada en la pantalla de inicio): mejor no ofrecer que
 * ofrecer algo que no va a llegar.
 */

function aBytes(base64url: string): Uint8Array {
  const relleno = '='.repeat((4 - (base64url.length % 4)) % 4);
  const b64 = (base64url + relleno).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

/**
 * Espera a que el trabajador quede activo. NO se usa `navigator.serviceWorker.ready`: ésa espera a un
 * trabajador que controle *esta* página, y el nuestro vive en `/assets/` a propósito — nunca la
 * controla, así que `ready` no llega jamás y el botón se quedaba en "Activando…" (lo cazó el recorrido
 * con Chrome real). Con tiempo límite: un botón que gira para siempre es peor que un error.
 */
function listoParaAvisar(reg: ServiceWorkerRegistration, ms = 10_000): Promise<void> {
  if (reg.active) return Promise.resolve();
  const w = reg.installing ?? reg.waiting;
  if (!w) return Promise.reject(new Error('sin trabajador'));
  return new Promise((ok, mal) => {
    const reloj = setTimeout(() => mal(new Error('tardó demasiado')), ms);
    w.addEventListener('statechange', () => {
      if (w.state === 'activated') {
        clearTimeout(reloj);
        ok();
      } else if (w.state === 'redundant') {
        clearTimeout(reloj);
        mal(new Error('trabajador descartado'));
      }
    });
  });
}

const soportado = () =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export function AvisarmeCuandoSalga({ token, yaSalio }: { token: string; yaSalio: boolean }) {
  const clave = `aviso-pedido-${token}`;
  const [clavePublica, setClavePublica] = useState<string | null>(null);
  const [estado, setEstado] = useState<'oculto' | 'ofrecer' | 'activando' | 'listo' | 'error'>('oculto');
  const [mensaje, setMensaje] = useState<string | null>(null);

  useEffect(() => {
    if (yaSalio || !soportado() || Notification.permission === 'denied') return;
    if (localStorage.getItem(clave)) {
      setEstado('listo');
      return;
    }
    fetch('/api/publico/aviso')
      .then((r) => r.json())
      .then((d) => {
        if (d?.activo && d.clavePublica) {
          setClavePublica(d.clavePublica);
          setEstado('ofrecer');
        }
      })
      .catch(() => undefined);
  }, [yaSalio, clave]);

  if (yaSalio || estado === 'oculto') return null;

  const activar = async () => {
    if (!clavePublica) return;
    setEstado('activando');
    setMensaje(null);
    try {
      const permiso = await Notification.requestPermission();
      if (permiso !== 'granted') {
        setEstado('error');
        setMensaje('Sin permiso no te podemos avisar. Revisa esta página de vez en cuando.');
        return;
      }
      const reg = await navigator.serviceWorker.register(urlTrabajador);
      await listoParaAvisar(reg);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: aBytes(clavePublica).buffer as ArrayBuffer });
      const r = await fetch(`/api/publico/pedido/${encodeURIComponent(token)}/aviso`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sub.toJSON()),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) {
        setEstado('error');
        setMensaje(d.error ?? 'No pudimos activar el aviso.');
        return;
      }
      localStorage.setItem(clave, '1');
      setEstado('listo');
    } catch {
      setEstado('error');
      setMensaje('Tu navegador no pudo activar el aviso. Revisa esta página de vez en cuando.');
    }
  };

  if (estado === 'listo') {
    return (
      <div className="mt-4 flex items-center gap-3 rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
        <Check className="h-5 w-5 shrink-0" aria-hidden />
        Listo: tu celular va a sonar cuando tu pedido salga.
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-3">
      <button
        className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 font-extrabold text-white disabled:opacity-60"
        onClick={() => void activar()}
        disabled={estado === 'activando'}
      >
        <BellRing className="h-5 w-5" aria-hidden />
        {estado === 'activando' ? 'Activando…' : 'Avísame cuando mi pedido salga'}
      </button>
      <p className="mt-2 text-center text-xs text-slate-700">Tu celular sonará cuando el repartidor vaya en camino.</p>
      {mensaje && <p className="mt-2 text-center text-sm text-red-700">{mensaje}</p>}
    </div>
  );
}
